/**
 * One studio (container): its config read structurally and edited as JSON,
 * checked while typing with the engine's schema (`lib/studio/config.ts`),
 * saved with optimistic concurrency — a save made from a version someone
 * else has since replaced comes back as a conflict, and the conflict dialog
 * offers their version or the user's edit on top of it.
 *
 * The editor's text, name and site are this page's state, seeded from the
 * studio as loaded (and re-seeded when a conflict loads theirs).
 */
import type { Studio } from '@acp/api-contract/studios.js';
import { ApiError } from '@acp/api-client/client.js';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { StudioConflict } from '../api/studios.js';
import { useGrants, useSaveStudio, useStudio } from '../app/hooks.js';
import { StudioConfigEditor } from '../components/studios/StudioConfigEditor.js';
import { StudioConfigSummary } from '../components/studios/StudioConfigSummary.js';
import { StudioConflictDialog } from '../components/studios/StudioConflictDialog.js';
import { Button } from '../components/ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card.js';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs.js';
import { allows } from '../lib/permissions.js';
import { checkConfigText, type ConfigProblem, configText, serverProblems, studioSummary } from '../lib/studio/config.js';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

type Draft = { readonly base: Studio; readonly name: string; readonly siteId: string; readonly text: string };

function draftOf(studio: Studio): Draft {
  return { base: studio, name: studio.name, siteId: studio.siteId ?? '', text: configText(studio.config) };
}

export function StudioView({ studioId }: { readonly studioId: string }) {
  const studio = useStudio(studioId);
  // Data first: a background refetch that fails must not replace the editor (and the user's unsaved edits).
  if (studio.data !== undefined) return <StudioEditorPage key={studio.data.studioId} loaded={studio.data} />;
  if (studio.error !== null) {
    return (
      <div className="mx-auto grid max-w-xl gap-4" role="alert" data-testid="studio-failed">
        <p className="text-sm text-destructive">Could not load the studio: {messageOf(studio.error)}</p>
        <Button variant="outline" className="w-fit" onClick={() => void studio.refetch()}>
          Retry
        </Button>
      </div>
    );
  }
  return <p className="p-8 text-center text-sm text-muted-foreground">Loading the studio…</p>;
}

function StudioEditorPage({ loaded }: { readonly loaded: Studio }) {
  const grants = useGrants();
  const save = useSaveStudio();
  const [draft, setDraft] = useState<Draft>(() => draftOf(loaded));
  const [serverIssues, setServerIssues] = useState<readonly ConfigProblem[] | null>(null);
  const [conflict, setConflict] = useState<Studio | null>(null);
  const check = useMemo(() => checkConfigText(draft.text), [draft.text]);
  const canSave = allows(grants, 'device.configure', { siteId: draft.base.siteId, studioId: draft.base.studioId });
  const dirty = draft.text !== configText(draft.base.config) || draft.name !== draft.base.name || draft.siteId !== (draft.base.siteId ?? '');
  const problems = check.ok ? (serverIssues ?? []) : check.problems;
  // The summary shows the draft while it is valid, else the saved version.
  const summary = check.ok ? studioSummary(check.config) : null;
  const savedCheck = useMemo(() => checkConfigText(configText(draft.base.config)), [draft.base]);

  const submit = (version: number): void => {
    if (!check.ok) return;
    setServerIssues(null);
    save.mutate(
      { studioId: draft.base.studioId, version, name: draft.name.trim(), siteId: draft.siteId.trim() === '' ? null : draft.siteId.trim(), config: check.document },
      {
        onSuccess: (saved) => {
          setDraft(draftOf(saved));
          toast.success(`Saved version ${String(saved.version)}`);
        },
        onError: (err) => {
          if (err instanceof StudioConflict) setConflict(err.current);
          else if (err instanceof ApiError && err.code === 'validation-failed') setServerIssues(serverProblems(err.issues));
          else toast.error(`Not saved: ${messageOf(err)}`);
        },
      },
    );
  };

  return (
    <div className="mx-auto grid max-w-6xl gap-6" data-testid="studio-page">
      <Card>
        <CardHeader>
          <CardTitle>{draft.base.name}</CardTitle>
          <CardDescription>
            <span className="font-mono">{draft.base.studioId}</span> · version {draft.base.version} · last written by {draft.base.updatedBy}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="overview">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="json" data-testid="studio-json-tab">
                Config (JSON)
              </TabsTrigger>
            </TabsList>
            <TabsContent value="overview" className="pt-4">
              {summary !== null ? (
                <StudioConfigSummary summary={summary} />
              ) : savedCheck.ok ? (
                <StudioConfigSummary summary={studioSummary(savedCheck.config)} />
              ) : (
                <p className="text-sm text-muted-foreground">The stored config does not parse with this build’s schema; see the JSON.</p>
              )}
            </TabsContent>
            <TabsContent value="json" className="pt-4">
              <StudioConfigEditor
                name={draft.name}
                siteId={draft.siteId}
                text={draft.text}
                problems={problems}
                problemSource={check.ok && serverIssues !== null ? 'server' : 'editor'}
                version={draft.base.version}
                dirty={dirty}
                canSave={canSave}
                pending={save.isPending}
                onName={(name) => setDraft({ ...draft, name })}
                onSite={(siteId) => setDraft({ ...draft, siteId })}
                onText={(text) => {
                  setServerIssues(null);
                  setDraft({ ...draft, text });
                }}
                onSave={() => submit(draft.base.version)}
                onRevert={() => {
                  setServerIssues(null);
                  setDraft(draftOf(draft.base));
                }}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      {conflict === null ? null : (
        <StudioConflictDialog
          open
          mine={draft.base.version}
          theirs={{ version: conflict.version, updatedBy: conflict.updatedBy, updatedAt: new Date(conflict.updatedAt).toLocaleString() }}
          onCancel={() => setConflict(null)}
          onLoadTheirs={() => {
            setDraft(draftOf(conflict));
            setConflict(null);
          }}
          onKeepMine={() => {
            // The edit, re-based on their version: saved over it.
            setDraft({ ...draft, base: conflict });
            setConflict(null);
            submit(conflict.version);
          }}
        />
      )}
    </div>
  );
}
